import { useState } from "react";
import { FormValidationHook } from "./FormValidation";

export interface Journey<T> {
    previousStep?: Nullish<T>;
    currentStep: T;
    nextStep?: Nullish<T>;
    canGoForward: boolean;
    goForward(): Promise<void>;
    canGoBack: boolean;
    goBack(): void;
    goToStep(index: number): void;
}

export default function useJourney<T>(steps: T[], onComplete: () => Promise<void>, formValidation?: FormValidationHook): Journey<T> {
    const [index, setIndex] = useState(0);

    const canGoForward = index < steps.length - 1;
    const canGoBack = index > 0;

    async function goForward() {
        if (formValidation?.current != null && !formValidation.current.validate()) return;

        formValidation?.current?.reset();

        if (!canGoForward) {
            await onComplete();
            return;
        };
        setIndex(m => m + 1);
    }

    function goBack() {
        if (!canGoBack) return;
        setIndex(m => m - 1);
    }

    function goToStep(stepIndex: number) {
        setIndex(stepIndex);
    }

    return {
        previousStep: canGoBack ? steps[index - 1] : null,
        currentStep: steps[index],
        nextStep: canGoForward ? steps[index + 1] : null,
        canGoForward,
        goForward,
        canGoBack,
        goBack,
        goToStep,
    }
}

enum StandardJourneyStep {
    Details,
    Confirmation
};

export interface StandardJourney extends Journey<StandardJourneyStep> {
    isShowingDetails: boolean;
    isShowingConfirmation: boolean;
}

export function useStandardJourney(onComplete: () => Promise<void>, formValidation?: FormValidationHook): StandardJourney {
    const journey = useJourney([StandardJourneyStep.Details, StandardJourneyStep.Confirmation], onComplete, formValidation);

    return {
        ...journey,
        isShowingDetails: journey.currentStep === StandardJourneyStep.Details,
        isShowingConfirmation: journey.currentStep === StandardJourneyStep.Confirmation
    }
}